(() => {
  const wishlistKey = 'amazingblock-wishlist';

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const updateWishlistCount = () => {
    const count = readWishlist().length;
    document.querySelectorAll('[data-ab-wishlist-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
  };

  const updateWishlistButtons = () => {
    const handles = readWishlist().map((item) => (typeof item === 'string' ? item : item.handle));
    document.querySelectorAll('[data-ab-wishlist-toggle]').forEach((button) => {
      const active = handles.includes(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(active));
    });
  };

  const saveWishlist = (items) => {
    try {
      localStorage.setItem(wishlistKey, JSON.stringify(items));
    } catch (error) {
      return;
    }
    updateWishlistCount();
    updateWishlistButtons();
  };

  const initializeHeroes = (root = document) => {
    root.querySelectorAll('[data-ab-hero]').forEach((hero) => {
      if (hero.dataset.initialized === 'true') return;
      hero.dataset.initialized = 'true';
      const slides = [...hero.querySelectorAll('[data-ab-hero-slide]')];
      const dots = [...hero.querySelectorAll('[data-ab-hero-dot]')];
      if (slides.length < 2) return;
      let current = 0;
      let timer;
      let paused = false;
      const show = (index) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === current));
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === current);
          dot.setAttribute('aria-current', dotIndex === current ? 'true' : 'false');
        });
      };
      const start = () => {
        if (paused || hero.dataset.autoplay !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        clearInterval(timer);
        timer = setInterval(() => show(current + 1), Number(hero.dataset.interval) || 6000);
      };
      hero.querySelector('[data-ab-hero-prev]')?.addEventListener('click', () => { show(current - 1); start(); });
      hero.querySelector('[data-ab-hero-next]')?.addEventListener('click', () => { show(current + 1); start(); });
      const pauseButton = hero.querySelector('[data-ab-hero-pause]');
      pauseButton?.addEventListener('click', () => {
        paused = !paused;
        pauseButton.setAttribute('aria-pressed', String(paused));
        pauseButton.setAttribute('aria-label', paused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel);
        if (paused) clearInterval(timer); else start();
      });
      dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.abHeroDot)); start(); }));
      hero.addEventListener('mouseenter', () => clearInterval(timer));
      hero.addEventListener('mouseleave', start);
      start();
    });
  };

  const initializeTabs = (root = document) => {
    root.querySelectorAll('[data-ab-tabs]').forEach((tabsRoot) => {
      tabsRoot.querySelectorAll('[data-ab-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const index = tab.dataset.abTab;
          tabsRoot.querySelectorAll('[data-ab-tab]').forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
          tabsRoot.querySelectorAll('[data-ab-panel]').forEach((panel) => { panel.hidden = panel.dataset.abPanel !== index; });
        });
      });
    });
  };

  const initializeLogoVideos = (root = document) => {
    root.querySelectorAll('[data-ab-logo-cycle]').forEach((cycle) => {
      if (cycle.dataset.initialized === 'true') return;
      cycle.dataset.initialized = 'true';
      let active = cycle.querySelector('[data-ab-logo-active]');
      const rest = cycle.querySelector('[data-ab-logo-rest]');
      let phaseTimer;
      const autoplay = cycle.dataset.autoplay === 'true';
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const replayDelay = Math.max(0, Number(cycle.dataset.replayDelay) || 0);
      const showRest = () => {
        if (rest) {
          active.hidden = true;
          rest.hidden = false;
        }
        phaseTimer = setTimeout(play, replayDelay);
      };
      const play = () => {
        clearTimeout(phaseTimer);
        rest?.setAttribute('hidden', '');
        active.hidden = false;
        active.currentTime = 0;
        active.play().catch(() => {});
      };
      active.addEventListener('ended', showRest);
      if (!autoplay || reducedMotion) {
        if (rest) {
          active.hidden = true;
          rest.hidden = false;
        }
        return;
      }
      play();
    });
  };

  const initializeMobileThemeMenus = () => {
    const destination = document.querySelector('[data-ab-mobile-theme-menus]');
    if (!destination) return;
    destination.querySelectorAll('[data-ab-mobile-theme-menu-clone]').forEach((item) => item.remove());
    document.querySelectorAll('.ab-header__desktop-nav [data-ab-theme-menu-item]').forEach((source) => {
      const clone = source.cloneNode(true);
      clone.dataset.abMobileThemeMenuClone = 'true';
      clone.removeAttribute('data-shopify-editor-block');
      const toggle = clone.querySelector('[data-ab-nav-toggle]');
      const submenu = clone.querySelector('[data-ab-submenu]');
      if (toggle && submenu) {
        const mobileId = `${submenu.id}-mobile`;
        submenu.id = mobileId;
        toggle.setAttribute('aria-controls', mobileId);
        toggle.setAttribute('aria-expanded', 'false');
        submenu.hidden = true;
      }
      destination.append(clone);
    });
  };

  const initializeTestimonials = (root = document) => {
    root.querySelectorAll('[data-ab-testimonials]').forEach((section) => {
      if (section.dataset.initialized === 'true') return;
      section.dataset.initialized = 'true';
      const viewport = section.querySelector('[data-ab-testimonials-viewport]');
      const track = section.querySelector('[data-ab-testimonials-track]');
      const originalCards = [...section.querySelectorAll('[data-ab-testimonial-card]')];
      const dots = [...section.querySelectorAll('[data-ab-testimonials-dot]')];
      if (!viewport || !track || !originalCards.length) return;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const originalCount = originalCards.length;
      if (!reducedMotion && originalCount > 1) {
        const before = originalCards.map((card) => {
          const clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('aria-current');
          return clone;
        });
        const after = originalCards.map((card) => {
          const clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('aria-current');
          return clone;
        });
        track.prepend(...before);
        track.append(...after);
      }
      const cards = [...track.querySelectorAll('[data-ab-testimonial-card]')];
      let current = reducedMotion || originalCount < 2 ? Math.floor(originalCount / 2) : originalCount + Math.floor(originalCount / 2);
      let timer;
      let paused = false;
      const getRealIndex = (index) => ((index % originalCount) + originalCount) % originalCount;
      const normalizeToMiddle = () => {
        if (reducedMotion || originalCount < 2) return;
        const realIndex = getRealIndex(current);
        if (current < originalCount || current >= originalCount * 2) {
          current = originalCount + realIndex;
          update(false);
        }
      };
      const update = (animate = true) => {
        if (!animate) {
          track.classList.add('is-normalizing');
          track.style.transition = 'none';
        }
        cards.forEach((card, index) => {
          const distance = Math.min(Math.abs(index - current), Math.abs(index - current + originalCount), Math.abs(index - current - originalCount), 2);
          card.dataset.distance = String(distance);
          card.setAttribute('aria-current', index === current ? 'true' : 'false');
        });
        const realIndex = getRealIndex(current);
        dots.forEach((dot, index) => dot.setAttribute('aria-current', index === realIndex ? 'true' : 'false'));
        if (reducedMotion) return;
        const activeCard = cards[current];
        const offset = viewport.clientWidth / 2 - (activeCard.offsetLeft + activeCard.offsetWidth / 2);
        track.style.transform = `translate3d(${offset}px,0,0)`;
        if (!animate) {
          void track.offsetWidth;
          track.style.transition = '';
          track.classList.remove('is-normalizing');
        }
      };
      const show = (index) => {
        current = index;
        update();
      };
      const stop = () => clearInterval(timer);
      const start = () => {
        stop();
        if (reducedMotion || paused || section.dataset.autoplay !== 'true' || originalCount < 2) return;
        timer = setInterval(() => show(current + 1), Number(section.dataset.interval) || 5000);
      };
      section.querySelector('[data-ab-testimonials-prev]')?.addEventListener('click', () => { show(current - 1); start(); });
      section.querySelector('[data-ab-testimonials-next]')?.addEventListener('click', () => { show(current + 1); start(); });
      dots.forEach((dot) => dot.addEventListener('click', () => { show(originalCount + Number(dot.dataset.abTestimonialsDot)); start(); }));
      track.addEventListener('transitionend', (event) => {
        if (event.target !== track || event.propertyName !== 'transform') return;
        normalizeToMiddle();
      });
      if (section.dataset.pauseHover === 'true') {
        section.addEventListener('mouseenter', stop);
        section.addEventListener('mouseleave', start);
        section.addEventListener('focusin', stop);
        section.addEventListener('focusout', start);
      }
      addEventListener('resize', () => update(false), { passive: true });
      update(false);
      start();
    });
  };

  const closePopup = (popup, remember = true) => {
    if (!popup) return;
    popup.dataset.open = 'false';
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ab-popup-open');
    popup._abPreviousFocus?.focus();
    if (remember) {
      try { localStorage.setItem(`ab-popup-${popup.id}`, String(Date.now())); } catch (error) { /* Storage may be unavailable. */ }
    }
  };

  const initializePopups = (root = document) => {
    root.querySelectorAll('[data-ab-popup]').forEach((popup) => {
      if (popup.dataset.initialized === 'true') return;
      popup.dataset.initialized = 'true';
      let lastClosed = 0;
      try { lastClosed = Number(localStorage.getItem(`ab-popup-${popup.id}`)) || 0; } catch (error) { lastClosed = 0; }
      if (lastClosed && Date.now() - lastClosed < Number(popup.dataset.frequency) * 86400000) return;
      setTimeout(() => {
        popup._abPreviousFocus = document.activeElement;
        popup.dataset.open = 'true';
        popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('ab-popup-open');
        popup.querySelector('input, button')?.focus();
      }, Number(popup.dataset.delay) * 1000);
    });
  };

  const initializeRecentlyViewed = (root = document) => {
    root.querySelectorAll('[data-ab-recent]').forEach((section) => {
      if (section.dataset.initialized === 'true') return;
      section.dataset.initialized = 'true';
      const key = 'amazingblock-recently-viewed';
      let items = [];
      try { const stored = JSON.parse(localStorage.getItem(key) || '[]'); if (Array.isArray(stored)) items = stored; } catch (error) { items = []; }
      const currentHandle = section.dataset.currentHandle;
      const visibleItems = items.filter((item) => item.handle && item.handle !== currentHandle).slice(0, Number(section.dataset.limit));
      const grid = section.querySelector('[data-ab-recent-grid]');
      visibleItems.forEach((item) => {
        const card = document.createElement('a'); card.className = 'ab-recent-card'; card.href = item.url;
        if (item.image) { const image = document.createElement('img'); image.src = item.image; image.alt = item.title; image.loading = 'lazy'; card.append(image); }
        const body = document.createElement('div'); body.className = 'ab-recent-card__body';
        const title = document.createElement('p'); title.className = 'ab-recent-card__title'; title.textContent = item.title;
        const price = document.createElement('p'); price.className = 'ab-recent-card__price'; price.textContent = item.price;
        body.append(title, price); card.append(body); grid.append(card);
      });
      section.hidden = visibleItems.length === 0;
      if (currentHandle) {
        const current = { handle: currentHandle, title: section.dataset.currentTitle, url: section.dataset.currentUrl, image: section.dataset.currentImage, price: section.dataset.currentPrice };
        try { localStorage.setItem(key, JSON.stringify([current, ...items.filter((item) => item.handle !== currentHandle)].slice(0, 12))); } catch (error) { /* Storage may be unavailable. */ }
      }
    });
  };

  const initializeStickyAtc = (root = document) => {
    root.querySelectorAll('[data-ab-sticky-atc]').forEach((bar) => {
      if (bar.dataset.initialized === 'true') return;
      bar.dataset.initialized = 'true';
      const update = () => { bar.dataset.visible = String(window.scrollY >= Number(bar.dataset.trigger)); };
      window.addEventListener('scroll', update, { passive: true });
      update();
      bar.querySelector('[data-ab-sticky-submit]')?.addEventListener('click', () => document.querySelector('product-form form[action*="/cart/add"]')?.requestSubmit());
    });
  };

  const initializeBackToTop = (root = document) => {
    root.querySelectorAll('[data-ab-back-to-top]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      const update = () => { button.dataset.visible = String(scrollY >= (Number(button.dataset.threshold) || 220)); };
      addEventListener('scroll', update, { passive: true });
      button.addEventListener('click', () => {
        if (!motionEnabled()) {
          scrollTo(0, 0);
          return;
        }
        cancelAnimationFrame(button._abScrollFrame);
        const start = scrollY;
        const startedAt = performance.now();
        const animate = (now) => {
          const progress = Math.min((now - startedAt) / 400, 1);
          const eased = .5 - Math.cos(progress * Math.PI) / 2;
          scrollTo(0, Math.round(start * (1 - eased)));
          if (progress < 1) button._abScrollFrame = requestAnimationFrame(animate);
        };
        button._abScrollFrame = requestAnimationFrame(animate);
      });
      update();
    });
  };

  const motionEnabled = () => !matchMedia('(prefers-reduced-motion: reduce)').matches && getComputedStyle(document.documentElement).getPropertyValue('--ab-motion-enabled').trim() === '1';

  const initializeMotion = (root = document) => {
    if (!motionEnabled()) return;
    const items = [...root.querySelectorAll('.ab-section > .ab-container, .ab-section > [class*="__inner"]')];
    const grids = [...root.querySelectorAll('.ab-section [class*="__grid"], .ab-section [class*="__list"]')];
    const targets = [...new Set([...items, ...grids])];
    grids.forEach((grid) => {
      grid.classList.add('ab-reveal-stagger');
      [...grid.children].slice(0, 8).forEach((child, index) => child.style.setProperty('--ab-stagger', index));
    });
    items.forEach((item) => item.classList.add('ab-reveal'));
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -8%', threshold: .08 });
    targets.forEach((target) => observer.observe(target));
  };

  const initializeHeaderMotion = () => {
    const header = document.querySelector('.ab-header');
    if (!header || header.dataset.motionReady) return;
    header.dataset.motionReady = 'true';
    const hero = document.querySelector('main[data-template="index"] .ab-hero--full');
    if (hero) {
      document.body.classList.add('ab-immersive-home');
      const announcement = document.querySelector('.ab-announcement');
      let chromeTimer;
      const showChrome = () => document.body.classList.add('ab-chrome-visible');
      const hideChrome = () => document.body.classList.remove('ab-chrome-visible');
      const isChromeHovered = () => header.matches(':hover') || announcement?.matches(':hover');
      const scheduleHide = () => {
        clearTimeout(chromeTimer);
        if (scrollY > 8) return;
        chromeTimer = setTimeout(() => {
          if (scrollY <= 8 && !isChromeHovered()) hideChrome();
        }, 1600);
      };
      const updateChrome = () => {
        clearTimeout(chromeTimer);
        if (scrollY > 8) {
          showChrome();
        } else {
          hideChrome();
        }
      };
      addEventListener('resize', updateChrome, { passive: true });
      addEventListener('scroll', updateChrome, { passive: true });
      [header, announcement].filter(Boolean).forEach((element) => {
        element.addEventListener('mouseenter', () => {
          clearTimeout(chromeTimer);
          showChrome();
        });
        element.addEventListener('mouseleave', scheduleHide);
        element.addEventListener('focusin', () => {
          clearTimeout(chromeTimer);
          showChrome();
        });
        element.addEventListener('focusout', scheduleHide);
      });
      updateChrome();
      return;
    }
    document.body.classList.remove('ab-immersive-home', 'ab-chrome-visible');
    if (!motionEnabled()) return;
    let previous = scrollY;
    addEventListener('scroll', () => {
      const current = scrollY;
      header.classList.toggle('is-compact', current > 80);
      header.classList.toggle('is-hidden', current > 160 && current > previous + 4);
      header.classList.toggle('is-returning', current < previous - 4);
      previous = current;
    }, { passive: true });
  };

  const initializeSuccessMotion = (root = document) => {
    if (!motionEnabled()) return;
    root.querySelectorAll('[data-ab-form-success]').forEach((message) => {
      if (message.dataset.celebrated) return;
      message.dataset.celebrated = 'true';
      const host = message.closest('form') || message.parentElement;
      host.style.position = 'relative';
      ['var(--ab-brand)','var(--ab-lego-blue)','var(--ab-lego-green)','var(--ab-lego-yellow)','var(--ab-lego-red)'].forEach((color, index) => {
        const particle = document.createElement('i');
        particle.className = 'ab-particle';
        particle.style.cssText = `--ab-particle-color:${color};--ab-px:${(index - 2) * 22}px;--ab-py:${-28 - Math.abs(index - 2) * 7}px;left:50%;top:50%`;
        host.append(particle);
        particle.addEventListener('animationend', () => particle.remove());
      });
    });
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab' || !container) return;
    const focusable = [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const closeDrawers = () => {
    document.querySelectorAll('[data-ab-drawer][data-open="true"]').forEach((drawer) => {
      drawer.dataset.open = 'false';
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ab-drawer-open');
      const trigger = document.querySelector(`[aria-controls="${drawer.id}"]`);
      trigger?.setAttribute('aria-expanded', 'false');
      (drawer._abPreviousFocus || trigger)?.focus();
    });
  };

  document.addEventListener('click', (event) => {
    const navToggle = event.target.closest('[data-ab-nav-toggle]');
    if (navToggle) {
      const submenu = navToggle.nextElementSibling;
      const willOpen = navToggle.getAttribute('aria-expanded') !== 'true';
      document.querySelectorAll('[data-ab-nav-toggle][aria-expanded="true"]').forEach((button) => {
        if (button === navToggle) return;
        button.setAttribute('aria-expanded', 'false');
        if (button.nextElementSibling?.matches('[data-ab-submenu]')) button.nextElementSibling.hidden = true;
      });
      navToggle.setAttribute('aria-expanded', String(willOpen));
      if (submenu?.matches('[data-ab-submenu]')) submenu.hidden = !willOpen;
    } else if (!event.target.closest('.ab-header__nav-item,.ab-header-drawer')) {
      document.querySelectorAll('[data-ab-nav-toggle][aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
        if (button.nextElementSibling?.matches('[data-ab-submenu]')) button.nextElementSibling.hidden = true;
      });
    }
    const popupClose = event.target.closest('[data-ab-popup-close]');
    if (popupClose) closePopup(popupClose.closest('[data-ab-popup]'));
    const wishlistButton = event.target.closest('[data-ab-wishlist-toggle]');
    if (wishlistButton) {
      const wishlist = readWishlist().filter((item) => item && typeof item === 'object');
      const index = wishlist.findIndex((item) => item.handle === wishlistButton.dataset.productHandle);
      if (index >= 0) wishlist.splice(index, 1);
      else wishlist.push({ handle: wishlistButton.dataset.productHandle, title: wishlistButton.dataset.productTitle, url: wishlistButton.dataset.productUrl });
      saveWishlist(wishlist);
    }
    const opener = event.target.closest('[data-ab-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.getAttribute('aria-controls'));
      if (drawer) {
        drawer._abPreviousFocus = opener;
        drawer.dataset.open = 'true';
        drawer.setAttribute('aria-hidden', 'false');
        opener.setAttribute('aria-expanded', 'true');
        document.body.classList.add('ab-drawer-open');
        drawer.querySelector('button, a')?.focus();
      }
    }
    if (event.target.closest('[data-ab-drawer-close]') || event.target.matches('[data-ab-drawer-overlay]')) closeDrawers();
  });

  document.addEventListener('keydown', (event) => {
    const activePopup = document.querySelector('[data-ab-popup][data-open="true"] .ab-popup__dialog');
    const activeDrawer = document.querySelector('[data-ab-drawer][data-open="true"] .ab-header-drawer__panel');
    trapFocus(event, activePopup || activeDrawer);
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-ab-nav-toggle][aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
        if (button.nextElementSibling?.matches('[data-ab-submenu]')) button.nextElementSibling.hidden = true;
      });
      closeDrawers();
      closePopup(document.querySelector('[data-ab-popup][data-open="true"]'));
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    updateWishlistCount();
    updateWishlistButtons();
    initializeHeroes(event.target);
    initializeTabs(event.target);
    initializeLogoVideos(event.target);
    initializeMobileThemeMenus();
    initializeTestimonials(event.target);
    initializePopups(event.target);
    initializeRecentlyViewed(event.target);
    initializeStickyAtc(event.target);
    initializeBackToTop(event.target);
    initializeMotion(event.target);
    initializeSuccessMotion(event.target);
  });
  document.addEventListener('DOMContentLoaded', () => {
    updateWishlistCount();
    updateWishlistButtons();
    initializeHeroes();
    initializeTabs();
    initializeLogoVideos();
    initializeMobileThemeMenus();
    initializeTestimonials();
    initializePopups();
    initializeRecentlyViewed();
    initializeStickyAtc();
    initializeBackToTop();
    initializeMotion();
    initializeHeaderMotion();
    initializeSuccessMotion();
  });
})();
